import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, TextField, TextArea, Section, EmptyState } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { Building2, Plus, Trash2, X, Save, Eye, EyeOff, Copy, ExternalLink } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function PortailsPromoteurs() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const canEdit = ["admin", "dirigeant", "manager_commercial", "secretaire_bo"].includes(user?.role);
  const canDelete = ["admin", "dirigeant"].includes(user?.role);

  const load = () => api.get("/portails-promoteurs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const select = (p) => { setSelected(p); setShowPwd(false); };

  const copy = (t) => { if (t) { navigator.clipboard.writeText(t); toast.success("Copié"); } };

  const save = async () => {
    if (!selected) return;
    try {
      await api.put(`/portails-promoteurs/${selected.id}`, {
        nom: selected.nom, site_url: selected.site_url, identifiant: selected.identifiant,
        mot_de_passe: selected.mot_de_passe, note: selected.note,
      });
      toast.success("Accès mis à jour");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async () => {
    if (!selected || !window.confirm(`Supprimer l'accès "${selected.nom}" ?`)) return;
    await api.delete(`/portails-promoteurs/${selected.id}`);
    toast.success("Accès supprimé");
    setSelected(null);
    load();
  };

  const normalizeUrl = (u) => (!u ? "#" : (u.startsWith("http") ? u : `https://${u}`));

  return (
    <div className="p-8" data-testid="portails-promoteurs-page">
      <Toaster position="top-right" richColors />
      <PageHeader
        title="Promoteurs"
        subtitle="Accès aux extranets promoteurs · Identifiants"
        actions={canEdit && (
          <PrimaryButton onClick={() => setShowForm(true)} data-testid="new-portail-button">
            <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Nouvel accès</span>
          </PrimaryButton>
        )}
      />

      <div className="bg-amber-50 border border-amber-200 px-4 py-3 rounded-sm text-sm text-amber-900 mb-6">
        Centralisez ici les <strong>accès aux extranets / sites internet de chaque promoteur</strong> : lien du portail, identifiant, mot de passe et note interne. Les mots de passe sont masqués par défaut.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Section title={`Liste (${items.length})`}>
            <div className="max-h-[600px] overflow-y-auto">
              {items.length === 0 ? <EmptyState icon={Building2} title="Aucun accès" description="Cliquez sur 'Nouvel accès' pour ajouter un portail promoteur." /> :
                items.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => select(p)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${selected?.id === p.id ? "bg-slate-50 border-l-2 border-l-[#B8873A]" : ""}`}
                    data-testid={`portail-item-${p.id}`}
                  >
                    <div className="text-sm font-semibold text-[#111111]">{p.nom}</div>
                    <div className="text-xs text-slate-500 mt-1 truncate">{p.site_url || "—"}</div>
                  </button>
                ))}
            </div>
          </Section>
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white rounded-sm border border-slate-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Accès promoteur</div>
                  <h2 className="text-xl font-bold text-[#111111]" style={{ fontFamily: "Cabinet Grotesk" }}>{selected.nom}</h2>
                </div>
                {canDelete && (
                  <SecondaryButton onClick={remove} className="!text-red-700 !border-red-300 hover:!bg-red-50" data-testid="delete-portail-btn">
                    <span className="flex items-center gap-1"><Trash2 className="w-3 h-3" /> Supprimer</span>
                  </SecondaryButton>
                )}
              </div>

              <div className="space-y-4">
                <TextField label="Nom du promoteur *" value={selected.nom || ""} onChange={(e) => setSelected({ ...selected, nom: e.target.value })} disabled={!canEdit} testid="portail-nom" />

                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-1.5">Site internet / extranet</div>
                  <div className="flex gap-2">
                    <input value={selected.site_url || ""} onChange={(e) => setSelected({ ...selected, site_url: e.target.value })} disabled={!canEdit} placeholder="https://extranet.promoteur.fr" className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50" data-testid="portail-site-url" />
                    {selected.site_url && (
                      <a href={normalizeUrl(selected.site_url)} target="_blank" rel="noopener noreferrer" className="px-3 py-2 border border-slate-300 rounded-sm hover:bg-slate-50" data-testid="portail-open-site"><ExternalLink className="w-4 h-4" /></a>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-1.5">Identifiant</div>
                  <div className="flex gap-2">
                    <input value={selected.identifiant || ""} onChange={(e) => setSelected({ ...selected, identifiant: e.target.value })} disabled={!canEdit} placeholder="Login / email de connexion" className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50" data-testid="portail-identifiant" />
                    <button type="button" onClick={() => copy(selected.identifiant)} className="px-3 py-2 border border-slate-300 rounded-sm hover:bg-slate-50" data-testid="portail-copy-identifiant"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-slate-700 mb-1.5">Mot de passe</div>
                  <div className="flex gap-2">
                    <input type={showPwd ? "text" : "password"} value={selected.mot_de_passe || ""} onChange={(e) => setSelected({ ...selected, mot_de_passe: e.target.value })} disabled={!canEdit} placeholder="••••••••" className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono disabled:bg-slate-50" data-testid="portail-mot-de-passe" />
                    <button type="button" onClick={() => setShowPwd((v) => !v)} className="px-3 py-2 border border-slate-300 rounded-sm hover:bg-slate-50" data-testid="portail-toggle-pwd">{showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                    <button type="button" onClick={() => copy(selected.mot_de_passe)} className="px-3 py-2 border border-slate-300 rounded-sm hover:bg-slate-50" data-testid="portail-copy-pwd"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>

                <TextArea label="Note interne" rows={3} value={selected.note || ""} onChange={(e) => setSelected({ ...selected, note: e.target.value })} disabled={!canEdit} testid="portail-note" />

                {canEdit && (
                  <div className="flex justify-end">
                    <PrimaryButton onClick={save} data-testid="save-portail-btn"><span className="flex items-center gap-2"><Save className="w-4 h-4" /> Enregistrer</span></PrimaryButton>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <EmptyState icon={Building2} title="Sélectionnez un accès" description="Ou créez-en un nouveau via le bouton en haut à droite." />
          )}
        </div>
      </div>

      {showForm && <PortailForm onClose={() => setShowForm(false)} onCreated={(p) => { setShowForm(false); load(); select(p); toast.success("Accès créé"); }} />}
    </div>
  );
}

function PortailForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ nom: "", site_url: "", identifiant: "", mot_de_passe: "", note: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/portails-promoteurs", form);
      onCreated(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm border border-slate-200 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="portail-form-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-[#111111]" style={{ fontFamily: "Cabinet Grotesk" }}>Nouvel accès promoteur</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <TextField label="Nom du promoteur *" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Bouygues Immobilier" testid="new-portail-nom" />
          <TextField label="Site internet / extranet" value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })} placeholder="https://extranet.promoteur.fr" testid="new-portail-url" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextField label="Identifiant" value={form.identifiant} onChange={(e) => setForm({ ...form, identifiant: e.target.value })} testid="new-portail-identifiant" />
            <TextField label="Mot de passe" value={form.mot_de_passe} onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })} testid="new-portail-pwd" />
          </div>
          <TextArea label="Note interne" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} testid="new-portail-note" />
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={onClose}>Annuler</SecondaryButton>
            <PrimaryButton type="submit" disabled={saving} data-testid="submit-portail-btn">{saving ? "…" : "Créer"}</PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}
